/**
 * Returns true if `text` contains only digits (and is non-empty).
 * Note: no `g` flag — avoids stateful `lastIndex` bug with `.test()`.
 */
export function checkPhoneNumber(text: string): boolean {
  const phoneRegex = /^\d+$/;
  return phoneRegex.test(text);
}

export type PhoneCheckCondition =
  | 'empty'
  | 'salesman'
  | 'tokoapp'
  | 'prefix'
  | 'format'
  | 'minLength'
  | 'maxLength';

/**
 * Validates a phone number and optional registration status.
 *
 * Returns `{ valid: true }` when the phone is acceptable,
 * or `{ valid: false, reason: string }` describing why it's not.
 *
 * Priority order:
 * 1. Empty check
 * 2. Salesman registration check
 * 3. Tokoapp registration check
 * 4. Prefix check (must start with '08')
 * 5. Digits-only format check
 * 6. Min/max length check
 *
 * Use `skip` to bypass specific conditions by name.
 */
export function checkPhoneNumberFormat({
  phone,
  registeredTokoapp,
  registeredSalesman,
  skip = [],
}: {
  phone: string;
  registeredTokoapp?: boolean;
  registeredSalesman?: boolean;
  skip?: Array<PhoneCheckCondition>;
}): { valid: boolean; reason?: string } {
  if (!skip.includes('empty') && !phone) {
    return { valid: false, reason: 'No. HP tidak boleh kosong' };
  }
  if (!skip.includes('salesman') && registeredSalesman) {
    return {
      valid: false,
      reason:
        'Nomor yang Anda masukkan sudah digunakan Salesman. Gunakan nomor lain',
    };
  }
  if (!skip.includes('tokoapp') && registeredTokoapp) {
    return {
      valid: false,
      reason:
        'Nomor telepon yang Anda masukkan sudah terdaftar. Harap gunakan nomor lain',
    };
  }
  if (!skip.includes('prefix') && !phone.startsWith('08')) {
    return {
      valid: false,
      reason: 'No. HP harus diawali dengan 08 (e.g. 085156263898)',
    };
  }
  if (!skip.includes('format') && !checkPhoneNumber(phone)) {
    return {
      valid: false,
      reason:
        'No. HP yang Anda masukkan tidak sesuai format (e.g. 085156263898)',
    };
  }
  if (!skip.includes('minLength') && phone.length < 8) {
    return { valid: false, reason: 'Minimal 8 digit angka' };
  }
  if (!skip.includes('maxLength') && phone.length > 14) {
    return { valid: false, reason: 'Maksimal 14 angka' };
  }
  return { valid: true };
}
